import type { ProductType } from '@mevaser/shared'
import { ApiError, isUnitTracked, queryKeys } from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  humanizeStatus,
  Input,
  Skeleton,
  STOCK_REQUEST_STATUS_TONE,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { ArrowLeft, ArrowLeftRight, Package, Pencil, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { SourceAvailability } from '../components/source-availability.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface StockRequestItem {
  id: string
  productId: string
  productName: string
  sku: string
  qty: number
  fulfilledQty: number | null
  note: string | null
}

interface ProductRow {
  id: string
  sku: string
  name: string
  type: ProductType
}

interface StockRequestDetail {
  id: string
  requestNumber: string
  orderId: string | null
  orderNumber: string | null
  status: string
  sourceLocationId: string
  sourceLocationName: string
  destinationLocationId: string
  destinationLocationName: string
  note: string | null
  createdByLabel: string | null
  items: StockRequestItem[]
  createdAt: string
  updatedAt: string
}

interface StockRequestEvent {
  id: string
  fromStatus: string | null
  toStatus: string
  actorType: string
  actorLabel: string | null
  note: string | null
  createdAt: string
}

const ACTION_LABEL: Record<string, string> = {
  accepted: 'Accept',
  declined: 'Decline',
  fulfilled: 'Fulfil',
  cancelled: 'Cancel',
}

/**
 * One request between two locations, and everything that can be done to it.
 * Same shape as an order's detail page: the legal moves come from the API,
 * not from a list kept here — a button offering a move the server will
 * refuse is worse than no button.
 */
export function StockRequestDetailPage() {
  const { requestId } = useParams({ from: '/authed/stock-requests/$requestId' })
  const { can } = useAuth()
  const [moving, setMoving] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)

  const { data: request, isLoading } = useQuery({
    queryKey: queryKeys.stockRequests.detail(requestId),
    queryFn: () => api.get<StockRequestDetail>(`/stock-requests/${requestId}`),
  })

  const { data: transitions } = useQuery({
    queryKey: queryKeys.stockRequests.transitions(requestId),
    queryFn: () =>
      api.get<{ from: string; to: string[] }>(`/stock-requests/${requestId}/transitions`),
    enabled: Boolean(request),
  })

  const { data: events } = useQuery({
    queryKey: queryKeys.stockRequests.events(requestId),
    queryFn: () => api.get<StockRequestEvent[]>(`/stock-requests/${requestId}/events`),
    enabled: Boolean(request),
  })

  if (isLoading || !request) {
    return (
      <Page>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-64 w-full" />
      </Page>
    )
  }

  // Accepting, declining and fulfilling are the source location's call — a
  // higher bar than merely being able to raise a request — so those three
  // only show up for someone who actually has it. The API enforces this too;
  // this just keeps a doomed button off the screen.
  const moves = (transitions?.to ?? []).filter((to) =>
    to === 'cancelled' ? can('inventory:request') : can('inventory:write'),
  )

  const isPartial =
    request.status === 'fulfilled' &&
    request.items.some((i) => i.fulfilledQty !== null && i.fulfilledQty < i.qty)

  return (
    <Page>
      <div className="pb-3">
        <Link
          to="/stock-requests"
          className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" /> All requests
        </Link>
      </div>

      {request.orderNumber ? (
        <Alert tone="info">
          Raised because{' '}
          {request.orderId ? (
            <Link
              to="/orders/$orderId"
              params={{ orderId: request.orderId }}
              className="font-medium underline"
            >
              {request.orderNumber}
            </Link>
          ) : (
            request.orderNumber
          )}{' '}
          was sold from {request.sourceLocationName}. Send the goods over and mark it fulfilled —
          the sale has already taken them off {request.sourceLocationName}&rsquo;s count, so
          fulfilling this moves no stock.
        </Alert>
      ) : null}

      <PageHeader
        title={request.requestNumber}
        description={`${request.destinationLocationName} asked ${request.sourceLocationName} · placed ${new Date(
          request.createdAt,
        ).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isPartial ? (
              <Badge tone="warning">Partially fulfilled</Badge>
            ) : (
              <Badge tone={STOCK_REQUEST_STATUS_TONE[request.status] ?? 'neutral'}>
                {humanizeStatus(request.status)}
              </Badge>
            )}
            {request.status === 'requested' && can('inventory:request') ? (
              <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                <Pencil className="size-3.5" aria-hidden="true" /> Edit
              </Button>
            ) : null}
            {moves.map((to) => (
              <Button
                key={to}
                size="sm"
                variant={to === 'cancelled' || to === 'declined' ? 'ghost' : 'primary'}
                onClick={() => setMoving(to)}
              >
                {ACTION_LABEL[to] ?? humanizeStatus(to)}
              </Button>
            ))}
          </div>
        }
      />

      {moves.length === 0 ? (
        <Alert tone="info" className="mb-4">
          This request is closed. Nothing further can be done to it.
        </Alert>
      ) : null}

      {isPartial ? (
        <Alert tone="warning" className="mb-4">
          This request was only partially fulfilled — the source did not have enough of everything
          asked for. See which items came up short below.
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="size-4" aria-hidden="true" /> Items requested
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {request.items.length === 0 ? (
                <EmptyState
                  icon={Package}
                  title="No items"
                  description="This request has no lines."
                />
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b border-border text-2xs uppercase tracking-wide text-foreground-subtle">
                    <tr>
                      <th className="px-4 py-2 text-start font-medium">Product</th>
                      <th className="px-4 py-2 text-end font-medium">
                        {request.status === 'fulfilled' ? 'Sent / Asked' : 'Qty'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {request.items.map((item) => {
                      const short = item.fulfilledQty !== null && item.fulfilledQty < item.qty
                      return (
                        <tr key={item.id} className="border-b border-border last:border-0">
                          <td className="px-4 py-2.5">
                            <p className="font-medium">{item.productName}</p>
                            <p className="font-identifier text-2xs text-foreground-muted">
                              {item.sku}
                            </p>
                            {item.note ? (
                              <p className="mt-0.5 text-xs text-foreground-muted">{item.note}</p>
                            ) : null}
                          </td>
                          <td className="px-4 py-2.5 text-end tabular">
                            {item.fulfilledQty !== null ? (
                              <span className={short ? 'font-medium text-warning' : undefined}>
                                {item.fulfilledQty} / {item.qty}
                                {short ? (
                                  <Badge tone="warning" className="ms-2">
                                    Short
                                  </Badge>
                                ) : null}
                              </span>
                            ) : (
                              item.qty
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>History</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {(events ?? []).map((event) => (
                  <li key={event.id} className="flex gap-3 text-sm">
                    <span
                      className="mt-1.5 size-1.5 shrink-0 rounded-full bg-border-strong"
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <p>
                        {event.fromStatus ? (
                          <>
                            <span className="text-foreground-muted">
                              {humanizeStatus(event.fromStatus)}
                            </span>{' '}
                            →{' '}
                          </>
                        ) : null}
                        <span className="font-medium">{humanizeStatus(event.toStatus)}</span>
                      </p>
                      <p className="text-2xs text-foreground-muted">
                        {event.actorLabel ?? event.actorType} ·{' '}
                        {new Date(event.createdAt).toLocaleString('en-US', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </p>
                      {event.note ? (
                        <p className="mt-0.5 text-xs text-foreground-muted">{event.note}</p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowLeftRight className="size-4" aria-hidden="true" /> Locations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Asking</p>
                <p className="font-medium">{request.destinationLocationName}</p>
              </div>
              <div>
                <p className="text-2xs uppercase tracking-wide text-foreground-subtle">
                  Being asked
                </p>
                <p className="font-medium">{request.sourceLocationName}</p>
              </div>
              {request.createdByLabel ? (
                <div>
                  <p className="text-2xs uppercase tracking-wide text-foreground-subtle">
                    Raised by
                  </p>
                  <p className="text-foreground-muted">{request.createdByLabel}</p>
                </div>
              ) : null}
            </CardContent>
          </Card>

          {request.note ? (
            <Card>
              <CardHeader>
                <CardTitle>Note</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-foreground-muted">{request.note}</CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      <TransitionDialog
        request={request}
        to={moving !== 'fulfilled' ? moving : null}
        onClose={() => setMoving(null)}
      />
      <FulfilDialog
        request={request}
        open={moving === 'fulfilled'}
        onClose={() => setMoving(null)}
      />
      <EditRequestDialog request={request} open={editing} onClose={() => setEditing(false)} />
    </Page>
  )
}

function TransitionDialog({
  request,
  to,
  onClose,
}: {
  request: StockRequestDetail
  to: string | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')

  const move = useMutation({
    mutationFn: () =>
      api.post(`/stock-requests/${request.id}/transition`, { to, note: note.trim() || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.stockRequests.all() })
      toast.success(`${request.requestNumber} is now ${humanizeStatus(to ?? '').toLowerCase()}`)
      setNote('')
      onClose()
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const isDestructive = to === 'declined' || to === 'cancelled'

  return (
    <Dialog open={to !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {to ? (ACTION_LABEL[to] ?? humanizeStatus(to)) : ''} — {request.requestNumber}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {to === 'cancelled' ? (
            <Alert tone="warning">No stock has moved yet — this just closes the request.</Alert>
          ) : null}

          <Field label="Note" hint="Kept on the request history.">
            {(props) => (
              <Textarea
                {...props}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={isDestructive ? 'danger' : 'primary'}
            onClick={() => move.mutate()}
            disabled={move.isPending}
          >
            {move.isPending ? 'Working…' : to ? (ACTION_LABEL[to] ?? humanizeStatus(to)) : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Fulfilling gets its own dialog rather than sharing the generic transition
 * one: it is the only move that can silently disappoint someone if it just
 * goes ahead, so it checks what is actually at the source first and makes a
 * shortfall something the person has to choose to accept, not something they
 * find out about afterward.
 */
function FulfilDialog({
  request,
  open,
  onClose,
}: {
  request: StockRequestDetail
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')

  useEffect(() => {
    if (!open) setNote('')
  }, [open])

  const { data: availability, isLoading } = useQuery({
    queryKey: queryKeys.stockRequests.availability(request.id),
    queryFn: () =>
      api.get<
        {
          productId: string
          productName: string
          sku: string
          requestedQty: number
          availableQty: number
        }[]
      >(`/stock-requests/${request.id}/availability`),
    enabled: open,
  })

  const shortItems = (availability ?? []).filter((a) => a.availableQty < a.requestedQty)
  const hasShortfall = shortItems.length > 0

  const fulfil = useMutation({
    mutationFn: () =>
      api.post<{
        shortfalls: { productName: string; fulfilledQty: number; requestedQty: number }[]
      }>(`/stock-requests/${request.id}/transition`, {
        to: 'fulfilled',
        note: note.trim() || null,
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.stockRequests.all() })
      // Fulfilling actually moved stock and (for handsets) units — the stock
      // and provisioning screens need to know their data is stale too.
      await queryClient.invalidateQueries({ queryKey: queryKeys.stock.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })

      if (result.shortfalls.length > 0) {
        toast.toast({
          tone: 'warning',
          title: `${request.requestNumber} is now fulfilled, but not in full`,
          description: result.shortfalls
            .map((s) => `${s.productName}: sent ${s.fulfilledQty} of ${s.requestedQty}`)
            .join('; '),
        })
      } else {
        toast.success(`${request.requestNumber} is now fulfilled`)
      }
      onClose()
    },
    onError: (error) =>
      toast.error('Could not fulfil', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Fulfil — {request.requestNumber}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Alert tone="warning">
            This will move stock from {request.sourceLocationName} to{' '}
            {request.destinationLocationName} right away.
          </Alert>

          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border text-2xs uppercase tracking-wide text-foreground-subtle">
                <tr>
                  <th className="py-1.5 text-start font-medium">Product</th>
                  <th className="py-1.5 text-end font-medium">Requested</th>
                  <th className="py-1.5 text-end font-medium">In stock</th>
                </tr>
              </thead>
              <tbody>
                {(availability ?? []).map((item) => {
                  const short = item.availableQty < item.requestedQty
                  return (
                    <tr key={item.productId} className="border-b border-border last:border-0">
                      <td className="py-1.5">
                        <p className="font-medium">{item.productName}</p>
                        <p className="font-identifier text-2xs text-foreground-muted">{item.sku}</p>
                      </td>
                      <td className="py-1.5 text-end tabular">{item.requestedQty}</td>
                      <td
                        className={`py-1.5 text-end tabular ${short ? 'font-medium text-danger' : ''}`}
                      >
                        {item.availableQty}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}

          {hasShortfall ? (
            <Alert tone="danger">
              There isn't enough {shortItems.map((s) => s.productName).join(', ')} at{' '}
              {request.sourceLocationName} to fulfil this in full. Fulfilling now sends only what's
              there and marks the rest as short — the request stays closed either way.
            </Alert>
          ) : null}

          <Field label="Note" hint="Kept on the request history.">
            {(props) => (
              <Textarea
                {...props}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={hasShortfall ? 'danger' : 'primary'}
            onClick={() => fulfil.mutate()}
            disabled={fulfil.isPending || isLoading}
          >
            {fulfil.isPending ? 'Working…' : hasShortfall ? 'Fulfil partially' : 'Fulfil'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface DraftItem {
  key: string
  productId: string
  sku: string
  name: string
  qty: number
}

/**
 * Changes what is being asked for — only ever shown while the request is
 * still `requested`, the same gate the API enforces. Same item-picker shape
 * as raising a new request, seeded from what is already on this one.
 */
function EditRequestDialog({
  request,
  open,
  onClose,
}: {
  request: StockRequestDetail
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [items, setItems] = useState<DraftItem[]>([])
  const [note, setNote] = useState('')

  // Fresh every time it opens, seeded from the request as it stands now.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberately only resets on open
  useEffect(() => {
    if (!open) return
    setSearch('')
    setNote(request.note ?? '')
    setItems(
      request.items.map((i) => ({
        key: i.id,
        productId: i.productId,
        sku: i.sku,
        name: i.productName,
        qty: i.qty,
      })),
    )
  }, [open])

  const { data: products } = useQuery({
    queryKey: queryKeys.products.list({ q: search, forStockRequestEdit: true }),
    queryFn: () =>
      api.get<{ items: ProductRow[] }>('/products', {
        query: { q: search || undefined, isActive: true, pageSize: 20, sort: 'name', order: 'asc' },
      }),
    enabled: open,
  })

  // Plans and services carry no stock — nothing to ask for.
  const stockedProducts = (products?.items ?? []).filter(
    (p) => p.type === 'stock' || isUnitTracked(p.type),
  )

  const addProduct = (product: ProductRow) => {
    setItems((current) => {
      if (current.some((i) => i.productId === product.id)) return current
      return [
        ...current,
        {
          key: crypto.randomUUID(),
          productId: product.id,
          sku: product.sku,
          name: product.name,
          qty: 1,
        },
      ]
    })
  }

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/stock-requests/${request.id}`, {
        note: note.trim() || null,
        items: items.map((i) => ({ productId: i.productId, qty: i.qty, note: null })),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.stockRequests.all() })
      toast.success('Request updated')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const valid = items.length > 0

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit {request.requestNumber}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search the catalogue by name or SKU…"
            />
            {search.trim() ? (
              <ul className="mt-1 max-h-40 divide-y divide-border overflow-y-auto rounded-md border border-border">
                {stockedProducts.map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-start text-sm hover:bg-surface-sunken"
                      onClick={() => addProduct(product)}
                    >
                      <span className="font-medium">{product.name}</span>{' '}
                      <span className="font-identifier text-xs text-foreground-muted">
                        {product.sku}
                      </span>
                    </button>
                  </li>
                ))}
                {stockedProducts.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-foreground-muted">No matches</li>
                ) : null}
              </ul>
            ) : null}
          </div>

          {items.length > 0 ? (
            <ul className="divide-y divide-border rounded-md border border-border">
              {items.map((item) => (
                <li key={item.key} className="flex items-center gap-2 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.name}</p>
                    <p className="font-identifier text-xs text-foreground-muted">{item.sku}</p>
                    <SourceAvailability
                      productId={item.productId}
                      sourceLocationId={request.sourceLocationId}
                      sourceName={request.sourceLocationName}
                      qty={item.qty}
                    />
                  </div>
                  <Input
                    type="number"
                    min={1}
                    value={item.qty}
                    onChange={(e) =>
                      setItems((current) =>
                        current.map((i) =>
                          i.key === item.key
                            ? { ...i, qty: Math.max(1, Number(e.target.value) || 1) }
                            : i,
                        ),
                      )
                    }
                    className="w-16 text-end"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${item.name}`}
                    onClick={() => setItems((current) => current.filter((i) => i.key !== item.key))}
                  >
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-foreground-muted">
              Search above and add what's needed — a request needs at least one item.
            </p>
          )}

          <Field label="Note">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Anything the other side should know"
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
